import { Injectable, Type } from '@angular/core';
import { ActivationEnd } from '@angular/router';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { LiveNavigationConfig, PrematchNavigationConfig } from '@frontend/sports/common/client-config-data-access';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { ColumnComponentRegistration, ColumnLayoutRegister, ColumnLayoutService, LayoutColumn } from '@frontend/sports/layout/feature';
import { Subscription, firstValueFrom } from 'rxjs';

import { EmptyComponent } from '../common/empty.component';
import { EventDetailsComponent } from './event-details.component';
import { LiveEventsNavigationComponent } from './live-events-navigation/live-events-navigation.component';
import { LiveEventsNavigationService } from './live-events-navigation/live-events-navigation.service';
import { NavigationStateService } from './navigation-state/navigation-state.service';
import { PrematchEventsNavigationComponent } from './prematch-events-navigation/prematch-events-navigation.component';
import { PreMatchEventsNavigationService } from './prematch-events-navigation/prematch-events-navigation.service';

@Injectable({ providedIn: 'root' })
export class NavigationLayoutService {
    private isRegistered: boolean;
    private selectedEvent: EventModel;
    private currentRegistered: Type<any>;
    private layoutChangedSubscription: Subscription | null;

    constructor(
        private columnLayoutService: ColumnLayoutService,
        private columnLayoutRegister: ColumnLayoutRegister,
        private routerEvents: RouterEventsService,
        private liveNavService: LiveEventsNavigationService,
        private prematchNavService: PreMatchEventsNavigationService,
        private eventDetailsStateService: NavigationStateService,
        private liveNavConfig: LiveNavigationConfig,
        private prematchNavConfig: PrematchNavigationConfig,
    ) {
        this.routerEvents.currentActivationEnd.subscribe((route) => this.activationEnd(route));
    }

    register(event: EventModel): void {
        this.selectedEvent = event;

        if (!this.layoutChangedSubscription) {
            this.layoutChangedSubscription = this.columnLayoutService.layoutChanged.subscribe(() => {
                if (this.canBeRegistered()) {
                    this.register(this.selectedEvent);
                } else {
                    this.unregister();
                }
            });
        }

        if (!this.canBeRegistered()) {
            return;
        }

        // check if the same navigation is already registered
        if (this.isCorrectNavRegistered(event)) {
            return;
        }

        this.unregister();

        if (event.live && this.liveNavConfig.isLiveEventsNavigationEnabled) {
            this.addLeftNavPlaceholder();
            this.registerLiveNav(event);
        } else if (!event.live && this.prematchNavConfig.isPrematchEventsNavigationEnabled) {
            this.addLeftNavPlaceholder();
            this.registerPrematchNav(event);
        }
    }

    unregister(): void {
        if (this.isRegistered) {
            this.removeLeftNavPlaceholder();
            this.columnLayoutService.remove(this.currentRegistered, LayoutColumn.Left);
            this.isRegistered = false;
            this.eventDetailsStateService.removeLeftNavigation();
        }
    }

    private async registerLiveNav(event: EventModel): Promise<void> {
        const tree = await this.liveNavService.initFromEvent(event);
        const componentData: ColumnComponentRegistration = {
            component: LiveEventsNavigationComponent,
            location: LayoutColumn.Left,
            inputs: { tree, event },
        };

        this.columnLayoutRegister.register(componentData);
        this.currentRegistered = componentData.component;
        this.isRegistered = true;
        this.eventDetailsStateService.setLeftNavigation(true);
    }

    private async registerPrematchNav(event: EventModel): Promise<void> {
        const tree = await firstValueFrom(this.prematchNavService.getNavigationTree(event));
        if (!tree?.children.length || !tree.children[0].children.length) {
            this.removeLeftNavPlaceholder();

            return;
        }

        const componentData: ColumnComponentRegistration = {
            component: PrematchEventsNavigationComponent,
            inputs: { tree, selectedEvent: event },
            location: LayoutColumn.Left,
        };

        this.columnLayoutRegister.register(componentData);
        this.currentRegistered = componentData.component;
        this.isRegistered = true;
        this.eventDetailsStateService.setLeftNavigation(false);
    }

    private canBeRegistered(): boolean {
        return this.columnLayoutService.layout.shouldHaveThreeColumns;
    }

    private addLeftNavPlaceholder(): void {
        const componentData: ColumnComponentRegistration = {
            component: EmptyComponent,
            location: LayoutColumn.Left,
        };
        this.columnLayoutRegister.register(componentData);
    }

    private removeLeftNavPlaceholder(): void {
        this.columnLayoutService.remove(EmptyComponent, LayoutColumn.Left);
    }

    private isCorrectNavRegistered(event: EventModel): boolean {
        if (!this.isRegistered) {
            return false;
        }

        if (this.eventDetailsStateService.isLiveNavRegistered && event.live) {
            return true;
        }

        const isPrematchEvent = this.prematchNavService.isEventSelectedFromPrematch || !event.live;
        const sameSport = this.prematchNavService.initialEvent?.sport.id === event.sport?.id;

        return this.eventDetailsStateService.isPrematchNavRegistered && isPrematchEvent && sameSport;
    }

    private activationEnd(route: ActivationEnd | undefined): void {
        if (route && route.snapshot.component !== EventDetailsComponent) {
            if (this.isRegistered) {
                this.unregister();
            }

            if (this.layoutChangedSubscription) {
                this.layoutChangedSubscription.unsubscribe();
                this.layoutChangedSubscription = null;
            }
        }
    }
}
